import type { ReactNode, SVGProps } from 'react';

/** Иконки один-в-один из макетов docs/design. */

type P = SVGProps<SVGSVGElement> & { size?: number };

function Stroke({ size = 22, sw = 1.9, children, ...rest }: P & { sw?: number; children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const Sparkle = ({ size = 18, double = false }: { size?: number; double?: boolean }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2c.6 4.8 2.2 6.4 7 7-4.8.6-6.4 2.2-7 7-.6-4.8-2.2-6.4-7-7 4.8-.6 6.4-2.2 7-7z" />
    {double && <path d="M19 15c.3 2 1 2.7 3 3-2 .3-2.7 1-3 3-.3-2-1-2.7-3-3 2-.3 2.7-1 3-3z" />}
  </svg>
);

export const Arrow = (p: P) => (
  <Stroke size={18} sw={2.4} stroke="var(--accent)" {...p}>
    <path d="M20 12H4M14 6l6 6-6 6" />
  </Stroke>
);

export const Refresh = (p: P) => (
  <Stroke size={18} sw={2.4} stroke="var(--accent)" {...p}>
    <path d="M4 12a8 8 0 1 0 3-6.2" />
    <path d="M4 4v4h4" />
  </Stroke>
);

export const Shield = (p: P) => (
  <Stroke size={18} sw={2.4} stroke="var(--accent)" {...p}>
    <path d="M12 3 4 6v6c0 4.5 3.4 8 8 9 4.6-1 8-4.5 8-9V6z" />
    <path d="m9 12 2 2 4-4" />
  </Stroke>
);

export const Plus = () => (
  <Stroke size={16} sw={2.8}>
    <path d="M12 5v14M5 12h14" />
  </Stroke>
);

export const Headset = () => (
  <Stroke size={20}>
    <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
    <rect x="3" y="14" width="4" height="6" rx="1.5" />
    <rect x="17" y="14" width="4" height="6" rx="1.5" />
  </Stroke>
);

export const Chevron = () => (
  <Stroke size={18} sw={2.6}>
    <path d="m9 5 7 7-7 7" />
  </Stroke>
);

export const PhotoIcon = () => (
  <Stroke>
    <rect x="3" y="4" width="18" height="16" rx="3" />
    <circle cx="9" cy="10" r="2" />
    <path d="m21 16-5-5-9 9" />
  </Stroke>
);

export const VideoIcon = () => (
  <Stroke>
    <rect x="3" y="6" width="13" height="12" rx="3" />
    <path d="m16 10 5-3v10l-5-3z" />
  </Stroke>
);

export const ShirtIcon = () => (
  <Stroke>
    <path d="M8 3 4 5 2 9l3 2v10h14V11l3-2-2-4-4-2a4 4 0 0 1-8 0z" />
  </Stroke>
);

export const EnhanceIcon = () => (
  <Stroke>
    <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
  </Stroke>
);

export const MusicIcon = () => (
  <Stroke>
    <path d="M9 18V5l11-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="17" cy="16" r="3" />
  </Stroke>
);

export const TrendUp = () => (
  <Stroke size={12} sw={3} stroke="#7CF2A8">
    <path d="m3 17 6-6 4 4 8-8" />
  </Stroke>
);

export const Play = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);

export const HomeIcon = () => (
  <Stroke sw={2}>
    <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" />
  </Stroke>
);

export const TwinIcon = () => (
  <Stroke sw={2}>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20c1-3.5 3.5-5.5 6.5-5.5s5.5 2 6.5 5.5" />
    <path d="M15.5 4.8a3.5 3.5 0 0 1 0 6.4" />
    <path d="M18 14.8c1.7.8 2.9 2.5 3.5 5.2" />
  </Stroke>
);

export const HistoryIcon = () => (
  <Stroke sw={2}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Stroke>
);

export const ProfileIcon = () => (
  <Stroke sw={2}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
  </Stroke>
);

export const BackIcon = () => (
  <Stroke size={20} sw={2.4}>
    <path d="m15 5-7 7 7 7" />
  </Stroke>
);

export const CardIcon = () => (
  <Stroke size={17} sw={2.2} stroke="#FFFFFF">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 10h18" />
  </Stroke>
);

export const CoinIcon = () => (
  <Stroke size={17} sw={2.2} stroke="#FFFFFF">
    <circle cx="12" cy="12" r="9" />
    <path d="M8 9h8M12 9v8" />
  </Stroke>
);

export const CheckIcon = ({ size = 56 }: { size?: number }) => (
  <Stroke size={size} sw={2.8}>
    <path d="m5 12 5 5 9-10" />
  </Stroke>
);

export const CrossIcon = ({ size = 52 }: { size?: number }) => (
  <Stroke size={size} sw={2.6}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Stroke>
);

export const ChevronDim = () => (
  <Stroke size={18} sw={2.4} stroke="rgba(255,255,255,0.6)">
    <path d="m9 5 7 7-7 7" />
  </Stroke>
);

export const CloseIcon = ({ size = 20 }: { size?: number }) => (
  <Stroke size={size} sw={2.4}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Stroke>
);

export const ImagePlusIcon = () => (
  <Stroke size={16} sw={2}>
    <rect x="3" y="4" width="18" height="16" rx="3" />
    <circle cx="9" cy="10" r="2" />
    <path d="m21 16-5-5-9 9" />
  </Stroke>
);

export const DownloadIcon = () => (
  <Stroke size={18} sw={2.2}>
    <path d="M12 3v13" />
    <path d="m7 11 5 5 5-5" />
    <path d="M5 20h14" />
  </Stroke>
);

export const ShareIcon = () => (
  <Stroke size={18} sw={2.2}>
    <path d="M12 3v13" />
    <path d="m7 8 5-5 5 5" />
    <path d="M5 14v6h14v-6" />
  </Stroke>
);

export const RepeatIcon = () => (
  <Stroke size={18} sw={2.2}>
    <path d="M4 12a8 8 0 1 0 3-6.2" />
    <path d="M4 4v4h4" />
  </Stroke>
);

export const SearchIcon = () => (
  <Stroke size={18} sw={2.2}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Stroke>
);

export const DuoIcon = () => (
  <Stroke size={18} sw={2}>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20c1-3.5 3.5-5.5 6.5-5.5s5.5 2 6.5 5.5" />
    <path d="M15.5 4.8a3.5 3.5 0 0 1 0 6.4" />
    <path d="M18 14.8c1.7.8 2.9 2.5 3.5 5.2" />
  </Stroke>
);

export const UploadIcon = () => (
  <Stroke size={18} sw={2}>
    <path d="M12 16V4" />
    <path d="m7 9 5-5 5 5" />
    <path d="M4 20h16" />
  </Stroke>
);

export const TickIcon = ({ size = 14 }: { size?: number }) => (
  <Stroke size={size} sw={3.2}>
    <path d="m5 12 5 5 9-10" />
  </Stroke>
);

export const MicIcon = () => (
  <Stroke size={17} sw={2.2}>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
  </Stroke>
);

export const LockIcon = () => (
  <Stroke size={16} sw={2.2}>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </Stroke>
);

export const ArrowSide = ({ dir }: { dir: 'left' | 'right' }) => (
  <Stroke size={18} sw={2.6}>
    <path d={dir === 'left' ? 'M19 12H5m6-6-6 6 6 6' : 'M5 12h14m-6-6 6 6-6 6'} />
  </Stroke>
);

export const PauseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="6" y="5" width="4" height="14" rx="1.2" />
    <rect x="14" y="5" width="4" height="14" rx="1.2" />
  </svg>
);

export const PlayFill = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M7 4.5v15a1 1 0 0 0 1.5.86l12-7.5a1 1 0 0 0 0-1.72l-12-7.5A1 1 0 0 0 7 4.5z" />
  </svg>
);

export const Back10Icon = () => (
  <Stroke size={20} sw={2.2}>
    <path d="M4 12a8 8 0 1 0 3-6.2" />
    <path d="M4 4v4h4" />
  </Stroke>
);

export const LyricsIcon = () => (
  <Stroke size={20} sw={2.2}>
    <path d="M4 6h16M4 12h10M4 18h7" />
  </Stroke>
);

export const SoundIcon = ({ muted }: { muted: boolean }) => (
  <Stroke size={18} sw={2.2}>
    <path d="M4 9v6h4l5 4V5L8 9z" />
    {muted ? <path d="m17 9 4 6m0-6-4 6" /> : <path d="M17 8.5a5 5 0 0 1 0 7" />}
  </Stroke>
);

export const CopyIcon = () => (
  <Stroke size={16} sw={2.2}>
    <rect x="8" y="8" width="12" height="12" rx="2" />
    <path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
  </Stroke>
);

export const DownArrowIcon = () => (
  <Stroke size={17} sw={2.2} stroke="#FFFFFF">
    <path d="M12 3v13" />
    <path d="m7 11 5 5 5-5" />
    <path d="M5 20h14" />
  </Stroke>
);

export const ClipIcon = () => (
  <Stroke size={17} sw={2.2} stroke="#FFFFFF">
    <rect x="3" y="6" width="13" height="12" rx="3" />
    <path d="m16 10 5-3v10l-5-3z" />
  </Stroke>
);

export const LinkIcon = () => (
  <Stroke size={17} sw={2.2} stroke="#FFFFFF">
    <path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1" />
    <path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1" />
  </Stroke>
);

export const SendIcon = () => (
  <Stroke size={19} sw={2.2}>
    <path d="m21 3-9.5 18-2.5-8-8-2.5z" />
    <path d="m21 3-12 10" />
  </Stroke>
);

export const CameraIcon = () => (
  <Stroke size={19} sw={2.2}>
    <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
    <circle cx="12" cy="13" r="3.5" />
  </Stroke>
);
